// 'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
  Switch,
} from '@heroui/react';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import { BiLeftArrowAlt, BiDownload, BiShow, BiFile, BiLinkExternal } from 'react-icons/bi';
import { FaFileSignature, FaNoteSticky } from 'react-icons/fa6';
import RepairListPDF from './RepairListPDF';
import { Report } from '../types';
import { Company } from '@/util/types/serverTypes';
import type { RepairItem } from '@/util/types/serverTypeCollection/repairList';
import { getImageAsDataUri } from '@/util/functions/imageProxy';
import tinycolor from 'tinycolor2';

interface RepairListExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  repairItems: RepairItem[];
  report: Report;
  company: Company;
  /** When true, "Include addendum items" switch starts checked. Default false. */
  defaultIncludeAddendum?: boolean;
  /** When provided, used as the PDF title instead of "Repair List". */
  repairListName?: string | null;
  /** When provided, show a link to the shareable web view (opens in new tab). */
  repairListId?: string | null;
  /** When provided with repairListId, appended as access_token so the view link is authenticated. */
  accessToken?: string | null;
  /** Hide the "View shareable web version" link (which points to /view). Used by callers that must not link to /view. */
  hideWebVersionLink?: boolean;
}

export default function RepairListExportModal({
  isOpen,
  onClose,
  repairItems,
  report,
  company,
  defaultIncludeAddendum = false,
  repairListName,
  repairListId,
  accessToken,
  hideWebVersionLink = false,
}: RepairListExportModalProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [logoDataUri, setLogoDataUri] = useState<string | null>(null);
  const [includeNotes, setIncludeNotes] = useState(true);
  const addendumCount = repairItems.filter(
    (i) => i.addendumType === 'repair' || i.addendumType === 'credit'
  ).length;
  const [includeAddendum, setIncludeAddendum] = useState(defaultIncludeAddendum);

  useEffect(() => {
    if (isOpen) setIncludeAddendum(defaultIncludeAddendum);
  }, [isOpen, defaultIncludeAddendum]);
  const itemsForPdf =
    includeAddendum && addendumCount > 0
      ? repairItems
      : repairItems.map((i) => ({
        ...i,
        addendumType: undefined,
        addendumCreditAmount: undefined,
      }));

  useEffect(() => {
    const fetchLogoAsDataUri = async () => {
      if (!company.logo) return;

      const dataUri = await getImageAsDataUri(company.logo);
      if (dataUri) {
        setLogoDataUri(dataUri);
      }
    };

    if (isOpen && company.logo) {
      fetchLogoAsDataUri();
    }
  }, [isOpen, company.logo]);

  const handleClose = () => {
    setShowPreview(false);
    setIsGenerating(false);
    onClose();
  };

  const generateFileName = () => {
    const date = new Date().toISOString().split('T')[0];
    const propertyName = report.name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return `repair-list-${propertyName}-${date}.pdf`;
  };

  const handlePreviewClick = () => {
    setIsGenerating(true);
    // Small delay to show loading state
    setTimeout(() => {
      setShowPreview(true);
      setIsGenerating(false);
    }, 500);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={showPreview ? '5xl' : 'xl'}
      scrollBehavior='inside'
      classNames={{
        base: 'max-h-[95vh]',
        body: 'p-0',
      }}
    >
      <ModalContent>
        {(onModalClose) => (
          <>
            <ModalHeader className='flex flex-col gap-1 border-b px-6 py-4'>
              <h2 className='text-xl font-bold text-gray-900'>
                Export Repair List
              </h2>
              <p className='text-sm text-gray-600'>
                {repairItems.length} items selected for export
              </p>
            </ModalHeader>

            <ModalBody>
              {!showPreview ? (
                // Export Options Screen
                <div className='space-y-6 p-6'>
                  <div className='text-center'>
                    <div
                      className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full'
                      style={{
                        backgroundColor: company.color || '#f0f9ff',
                      }}
                    >
                      <BiFile
                        className='h-10 w-10'
                        color={
                          tinycolor(company.color).isDark() ? '#fff' : '#000'
                        }
                      />
                    </div>
                    <h3 className='mb-2 text-lg font-semibold text-gray-900'>
                      Ready to Export
                    </h3>
                    <p className='text-gray-600'>
                      Your repair list contains {repairItems.length} items and
                      includes all inspection details, photos, and your custom
                      notes.
                    </p>
                  </div>

                  <div className='rounded-lg border bg-gray-50 p-4'>
                    <h4 className='mb-2 font-medium text-gray-900'>
                      Export Details
                    </h4>
                    <ul className='space-y-1 text-sm text-gray-600'>
                      <li>• Property: {report.name}</li>
                      <li>• Inspection Date: {report.inspection.date}</li>
                      <li>• Inspector: {report.inspectors[0]?.name}</li>
                      <li>• Company: {company.name}</li>
                      <li>• Total Items: {repairItems.length}</li>
                    </ul>
                  </div>

                  <div className='space-y-3'>
                  {addendumCount > 0 && (
                    <div className='flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3'>
                      <div className='flex gap-3'>
                        <span className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#fdece0] text-main'>
                          <FaFileSignature className='text-sm' />
                        </span>
                        <div>
                          <p className='text-sm font-medium text-gray-900'>Include addendum items</p>
                          <p className='text-xs text-gray-500'>
                            Repair and credit choices from your addendum will appear in the PDF and web
                            view. Turn off to export without addendum details.
                          </p>
                        </div>
                      </div>
                      <Switch
                        isSelected={includeAddendum}
                        onValueChange={setIncludeAddendum}
                        classNames={{ base: 'shrink-0' }}
                        aria-label='Include addendum items'
                      />
                    </div>
                  )}

                  <div className='flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3'>
                    <div className='flex gap-3'>
                      <span className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#fdece0] text-main'>
                        <FaNoteSticky className='text-sm' />
                      </span>
                      <div>
                        <p className='text-sm font-medium text-gray-900'>Include your custom notes</p>
                        <p className='text-xs text-gray-500'>
                          Your custom notes added to each item will appear in the PDF and web view.
                          Turn off to share a cleaner version without internal comments.
                        </p>
                      </div>
                    </div>
                    <Switch
                      isSelected={includeNotes}
                      onValueChange={setIncludeNotes}
                      classNames={{ base: 'shrink-0' }}
                      aria-label='Include your custom notes'
                    />
                  </div>
                  </div>

                  <div className='flex flex-col gap-3'>
                    {!hideWebVersionLink && (
                      <Button
                        as={repairListId ? Link : undefined}
                        href={repairListId ? (() => {
                          const params = new URLSearchParams();
                          if (accessToken) params.set('access_token', accessToken);
                          params.set('mode', 'report');
                          if (!includeNotes) params.set('hide_notes', '1');
                          const qs = params.toString();
                          return `/repair-list/${repairListId}/view${qs ? `?${qs}` : ''}`;
                        })() : undefined}
                        target='_blank'
                        rel='noopener noreferrer'
                        variant='flat'
                        color='success'
                        size='lg'
                        className='w-full font-bold uppercase'
                        endContent={<BiLinkExternal className='h-5 w-5' />}
                        isDisabled={!repairListId}
                      >
                        <span className='flex flex-col items-center leading-tight'>
                          <span>View shareable web version</span>
                          {!repairListId && (
                            <span className='text-[10px] font-normal normal-case opacity-70'>
                              Save repair list to view
                            </span>
                          )}
                        </span>
                      </Button>
                    )}
                    <Button
                      color='primary'
                      size='lg'
                      className='w-full font-bold uppercase'
                      startContent={
                        isGenerating ? (
                          <Spinner size='sm' color='white' />
                        ) : (
                          <BiShow className='h-5 w-5' />
                        )
                      }
                      onPress={handlePreviewClick}
                      isDisabled={isGenerating}
                    >
                      {isGenerating ? 'Generating Preview...' : 'Preview PDF'}
                    </Button>

                    <Button
                      variant='light'
                      size='lg'
                      className='w-full font-bold uppercase'
                      onPress={onModalClose}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // PDF Preview Screen
                <div className='flex h-[80vh] flex-col '>
                  {/* Preview Header */}
                  <div className='flex flex-col gap-3 border-b bg-gray-50 p-4'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-3'>
                        <Button
                          isIconOnly
                          variant='light'
                          size='sm'
                          onPress={() => setShowPreview(false)}
                          aria-label='Back to options'
                        >
                          <BiLeftArrowAlt className='h-4 w-4' />
                        </Button>
                        <div>
                          <h3 className='font-semibold text-gray-900'>
                            PDF Preview
                          </h3>
                          <p className='text-xs text-gray-600'>
                            {generateFileName()}
                          </p>
                        </div>
                      </div>

                      <PDFDownloadLink
                        key={`${includeAddendum}-${includeNotes}`}
                        document={
                          <RepairListPDF
                            repairItems={itemsForPdf}
                            report={report}
                            company={company}
                            logoDataUri={logoDataUri}
                            repairListName={repairListName}
                            includeNotes={includeNotes}
                          />
                        }
                        fileName={generateFileName()}
                      >
                        {({ loading }) => (
                          <Button
                            color='primary'
                            size='md'
                            startContent={
                              loading ? (
                                <Spinner size='sm' color='white' />
                              ) : (
                                <BiDownload className='h-4 w-4' />
                              )
                            }
                            isDisabled={loading}
                            className='font-semibold'
                          >
                            {loading ? 'Preparing...' : 'Download PDF'}
                          </Button>
                        )}
                      </PDFDownloadLink>
                    </div>
                  </div>

                  {/* PDF Viewer */}
                  <div className='flex-1 bg-gray-100'>
                    <PDFViewer
                      key={`${includeAddendum}-${includeNotes}`}
                      width='100%'
                      height='100%'
                      className='border-0'
                      showToolbar={false}
                    >
                      <RepairListPDF
                        repairItems={itemsForPdf}
                        report={report}
                        company={company}
                        logoDataUri={logoDataUri}
                        repairListName={repairListName}
                        includeNotes={includeNotes}
                      />
                    </PDFViewer>
                  </div>
                </div>
              )}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
